import React from 'react';
import { useEditor } from '../contexts/EditorContext';
import { useLanguage } from '../i18n';
import { ShapeElement } from '../types/elements';

interface AddShapeElementProps {
  shapeType: 'rectangle' | 'circle' | 'triangle';
  onClose: () => void;
}

const AddShapeElement: React.FC<AddShapeElementProps> = ({ shapeType, onClose }) => {
  const { addElement, elements } = useEditor();
  const { t } = useLanguage();
  const [width, setWidth] = React.useState(100);
  const [height, setHeight] = React.useState(100);
  const [color, setColor] = React.useState('#1e48e0');
  const [borderRadius, setBorderRadius] = React.useState(0);

  const getNextName = () => {
    let idx = 1;
    const base = 'shape-';
    while (elements.some(e => e.name === base + idx)) idx++;
    return base + idx;
  };
  const [name, setName] = React.useState(getNextName());

  const handleAdd = () => {
    let finalName = name.replace(/[^a-zA-Z0-9\-_]/g, '');
    if (!finalName.startsWith('shape-')) {
      finalName = 'shape-' + finalName.replace('shape-', '');
    }
    if (!finalName || finalName === 'shape-') finalName = getNextName();
    
    const id = Date.now().toString();
    const order = elements.length;
    
    const newShape: ShapeElement = {
      id,
      type: 'shape',
      order,
      x: 50,
      y: 50 + order * 40,
      shapeType,
      width,
      height,
      color,
      name: finalName,
      ...(shapeType === 'rectangle' && { borderRadius }),
    };
    
    addElement(newShape);
    onClose();
  };

  return (
    <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
      <h3 style={{ color: '#d4d4d8', margin: '0 0 12px 0', fontSize: 14, fontWeight: 600 }}>
        {t('components.addShape.title', { shape: t(`components.toolbar.${shapeType}`) })}
      </h3>
      
      <div>
        <label style={{ display: 'block', marginBottom: 4, color: '#a1a1aa', fontSize: 11 }}>
          {t('components.addShape.name')}
        </label>
        <input
          value={name}
          onChange={e => setName(e.target.value.replace(/[^a-zA-Z0-9\-_]/g, ''))}
          placeholder="shape-1"
          style={{
            width: '100%',
            padding: '6px 8px',
            background: '#0a0a0a',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: 4,
            color: '#d4d4d8',
            fontSize: 11,
          }}
        />
      </div>

      <div>
        <label style={{ display: 'block', marginBottom: 4, color: '#a1a1aa', fontSize: 11 }}>
          {t('components.addShape.width')}
        </label>
        <input
          type="number"
          min={10}
          max={2000}
          value={width}
          onChange={e => setWidth(Number(e.target.value))}
          style={{
            width: '100%',
            padding: '6px 8px',
            background: '#0a0a0a',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: 4,
            color: '#d4d4d8',
            fontSize: 11,
          }}
        />
      </div>

      <div>
        <label style={{ display: 'block', marginBottom: 4, color: '#a1a1aa', fontSize: 11 }}>
          {t('components.addShape.height')}
        </label>
        <input
          type="number"
          min={10}
          max={2000}
          value={height}
          onChange={e => setHeight(Number(e.target.value))}
          style={{
            width: '100%',
            padding: '6px 8px',
            background: '#0a0a0a',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: 4,
            color: '#d4d4d8',
            fontSize: 11,
          }}
        />
      </div>

      {shapeType === 'rectangle' && (
        <div>
          <label style={{ display: 'block', marginBottom: 4, color: '#a1a1aa', fontSize: 11 }}>
            {t('components.addShape.borderRadius')}
          </label>
          <input
            type="number"
            min={0}
            max={50}
            value={borderRadius}
            onChange={e => setBorderRadius(Number(e.target.value))}
            style={{
              width: '100%',
              padding: '6px 8px',
              background: '#0a0a0a',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: 4,
              color: '#d4d4d8',
              fontSize: 11,
            }}
          />
        </div>
      )}

      <div>
        <label style={{ display: 'block', marginBottom: 4, color: '#a1a1aa', fontSize: 11 }}>
          {t('components.addShape.color')}
        </label>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input
            type="color"
            value={color}
            onChange={e => setColor(e.target.value)}
            style={{
              width: 50,
              height: 40,
              padding: 0,
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: 4,
              cursor: 'pointer',
            }}
          />
          <input
            type="text"
            value={color}
            onChange={e => setColor(e.target.value)}
            placeholder="#1e48e0"
            style={{
              flex: 1,
              padding: '6px 8px',
              background: '#0a0a0a',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: 4,
              color: '#d4d4d8',
              fontSize: 11,
            }}
          />
        </div>
      </div>

      <button
        onClick={handleAdd}
        style={{
          width: '100%',
          padding: '10px',
          background: '#1e48e0',
          color: '#fff',
          border: 'none',
          borderRadius: 6,
          cursor: 'pointer',
          fontWeight: 600,
          fontSize: 12,
          marginTop: 8,
        }}
        onMouseEnter={e => e.currentTarget.style.background = '#163aa8'}
        onMouseLeave={e => e.currentTarget.style.background = '#1e48e0'}
      >
        {t('components.addShape.add')}
      </button>
    </div>
  );
};

export default AddShapeElement;

